import { observable, action, computed, isObservable } from 'mobx';

import { unObserve } from 'core/utils';

import { AppManager, Variables } from 'core/index';
import { client } from 'core/client';

import { env } from 'process';

import {
  // ConversationsConnectionQuery,
  InboxPollQuery,
  CONVERSATIONS_CONNECTION_QUERY,
} from './gql';
import { ConversationsApp, ConversationModel } from './app';

export class ConversationsConnManager extends AppManager {
  @observable pageNum = 0;
  @observable nodes = [];
  @observable total = 0;
  @observable hasDataBeenLoaded = false;

  constructor(parent, init, options) {
    super(parent, init, options);
    this.batchSize = 10;
    this.initialBatchSize = 15;

    this.variables = this.variables.extend(
      new Variables({
        // spaceId: () => clientStore.activeSpaceId,

        // offset: () => this.pageNum * this.batchSize,
        // first: () => this.batchSize,
        offset: () => 0,
        first: () => this.initialBatchSize + this.batchSize * this.pageNum,
      }),
    );
  }

  process = queryResult => {
    const Model = ConversationModel;
    const result = {};
    if (queryResult) {
      result.nodes = queryResult.data.conversations.nodes.map(node =>
        Model.Load(node),
      );
      result.total = queryResult.data.conversations.total;
    }
    return result;
  };

  // process = queryResult => {
  //   const Model = ConversationModel;
  //   if (queryResult) {
  //     queryResult.nodes = queryResult.nodes.map(node => Model.Load(node));
  //   }
  //   return queryResult;
  // };

  @action SetConversations = res => {
    if (res) {
      this.hasDataBeenLoaded = true;
      this.total = res.total;

      if (this.nodes.length <= res.nodes.length) {
        this.nodes = res.nodes;
      } else {
        // If we got a smaller set of data than what we currently have, put the new data at the start
        // and remove the conversations from the previous data that have an ID of something we just got.
        const newDataIds = {};
        res.nodes.forEach(element => {
          newDataIds[element.id] = true;
        });
        const previousData = this.nodes.filter(
          conversation => !newDataIds[conversation.id],
        );
        this.nodes = [...res.nodes, ...previousData];
      }
    }
  };

  queryFromStart = () => {
    this.pageNum = 0;

    const context = {
      abortOnRerequest: true,
      queryDeduplication: false,
    };
    const GQL = CONVERSATIONS_CONNECTION_QUERY;
    client
      .query({
        query: GQL,
        variables: isObservable(this.variables)
          ? unObserve(this.variables, ['offset', 'first'])
          : this.variables,
        fetchPolicy: 'network-only', // 'cache-first'
        context,
      })
      .then(this.process)
      .then(this.SetConversations)
      .catch(error => {
        if (env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log(
            'CONVERSATIONS_CONNECTION_QUERY.queryFromStart error',
            error,
          );
        }
      });
  };

  fetchMore = () => {
    this.pageNum += 1;

    const context = {
      abortOnRerequest: true,
      queryDeduplication: false,
    };

    client
      .query({
        query: CONVERSATIONS_CONNECTION_QUERY,
        variables: isObservable(this.variables)
          ? unObserve(this.variables, ['offset', 'first'])
          : this.variables,
        fetchPolicy: 'network-only', // 'cache-first'
        context,
      })

      .then(this.process)
      .then(this.SetConversations)
      .catch(error => {
        if (env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('CONVERSATIONS_CONNECTION_QUERY.fetchMore error', error);
        }
      });
  };

  // queryFromStart = () => {
  //   this.pageNum = 0;
  //   // this.initialiseQuery(ConversationsConnectionQuery, this.variables);
  //   if (this.query) {
  //     this.query.disposer();
  //   }

  //   this.query = new ConversationsConnectionQuery(this, {
  //     variables: this.variables,
  //     onSysErr: this.PageLoadErr,
  //     abortOnRerequest: true,
  //   });
  //   this.query.process(this.process, this.SetConversations);
  //   this.query.query();
  // };

  // fetchMore = () => {
  //   this.pageNum += 1;

  //   if (this.moreQuery) {
  //     this.moreQuery.disposer();
  //   }
  //   this.moreQuery = new ConversationsConnectionQuery(this, {
  //     variables: this.variables,
  //     onSysErr: this.PageLoadErr,
  //     abortOnRerequest: true,
  //   });
  //   this.moreQuery.process(this.process, this.SetConversations);
  //   // for some bizarre reason this triggers two requests when used with moreQuery de-duplication 'false'
  //   this.moreQuery.query();
  // };
}

const INBOX_POLL_INTERVAL = 10000;
const INBOX_QUERY_DEBOUNCE_MS = 10000;

export default class InboxAppManager extends AppManager {
  static registryName = 'InboxAppManager';

  @observable conversationsConnManager = null;
  @observable inboxPollQuery = null;
  @observable totalUnread = 0;
  @observable total = 0;
  @observable lastUnreadCount = 0;
  @observable lastUnreadCount = 0;

  @observable isInboxOpen = false;

  lastTimeInboxWasQueried = Infinity;

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('conversations', ConversationsApp);

    // this.variables = new Variables({});
    this.conversationsConnManager = new ConversationsConnManager(
      this,
      {
        // initialBatchSize
        // batchSize
        // sortBy
        // loadMoreQuery
      },
      { autoDispose: false },
    );
  }

  @action QueryInbox() {
    this.lastTimeInboxWasQueried = Date.now();
    this.conversationsConnManager.queryFromStart();
  }

  @action ToggleInbox = forceOpenState => {
    // if it's opening maybe query
    if (!this.isInboxOpen && forceOpenState !== false) this.MaybeQueryInbox();

    if (typeof forceOpenState === 'boolean') this.isInboxOpen = forceOpenState;
    else this.isInboxOpen = !this.isInboxOpen;
  };

  @computed get isPolling() {
    return !!this.inboxPollQuery;
  }

  @action MaybeQueryInbox = () => {
    if (Date.now() - this.lastTimeInboxWasQueried < INBOX_QUERY_DEBOUNCE_MS)
      return;
    this.QueryInbox();
  };

  @action NewInboxPollQuery = () => {
    // this is the initial Inbox call triggered by frameStore setting up polling when user signs in
    this.MaybeQueryInbox();

    this.inboxPollQuery = new InboxPollQuery(this, {
      queryKey: 'inboxPoll',
      pollInterval: INBOX_POLL_INTERVAL,
      fetchPolicy: 'network-only',
      hackInit: 'hackValue',
    });
    this.inboxPollQuery.process(
      result => ({
        totalUnread: result ? result.totalUnread : 0,
        total: result ? result.total : 0,
      }),
      ({ totalUnread, total }) => {
        this.totalUnread = totalUnread;
        this.total = total;
        if (
          this.lastUnreadCount !== this.totalUnread ||
          this.lastTotal !== this.total ||
          Date.now() - this.lastTimeInboxWasQueried >
            INBOX_QUERY_DEBOUNCE_MS * 10
        ) {
          this.QueryInbox();
        }
        this.lastUnreadCount = this.totalUnread;
        this.lastTotal = this.total;
      },
    );
    this.inboxPollQuery.query();
  };

  @computed get notificationBadgeNum() {
    // return this.totalUnread;

    // NOTE(david): This whole block is a local hack due to missing server data.
    // the condition " && conversation.lastMessage" is a further hack because such
    // conversations will get skipped by the Inbox, even though they should not exist.
    return this.conversationsConnManager.nodes.reduce((acc, conversation) => {
      if (conversation.unreadCount > 0 && conversation.lastMessage)
        return acc + 1;
      return acc;
    }, 0);
  }

  @action StopInboxPollQuery = () => {
    if (this.inboxPollQuery) {
      this.inboxPollQuery.disposer();
      this.inboxPollQuery = null;
    }
  };

  disposeWhen() {
    return false;
  }
}
